import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import './ContactUs.css'
import './ContactUs-responsive.css';

import { ProgressSpinner } from 'primereact/progressspinner';
import { Ripple } from 'primereact/ripple';
import { Slide, Fade, Zoom } from "react-awesome-reveal";
import { Toast } from "primereact/toast";
import Tilt from 'react-parallax-tilt';

import { goToTop } from "../../../WebApp/Components/goToTop";

const ContactUs: React.FC = () => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [message, setMessage] = useState<string>('');

    const [firstNameError, setFirstNameError] = useState<string>('');
    const [lastNameError, setLastNameError] = useState<string>('');
    const [phoneNumberError, setPhoneNumberError] = useState<string>('');
    const [subjectError, setSubjectError] = useState<string>('');
    const [messageError, setMessageError] = useState<string>('');

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [hasError, setHasError] = useState<boolean>(false);

    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    const validateFirstName = (): boolean => {
        if (!firstName.trim()) {
            setFirstNameError("First Name is required.");
            return false;
        }
        setFirstNameError("");
        return true;
    };

    const validateLastName = (): boolean => {
        if (!lastName.trim()) {
            setLastNameError("Last Name is required.");
            return false;
        }
        setLastNameError("");
        return true;
    };

    const validatePhoneNumber = (): boolean => {
        if (!phoneNumber.trim()) {
            setPhoneNumberError("Phone Number is required.");
            return false;
        }
        if (phoneNumber.length < 11 || phoneNumber.length > 13) {
            setPhoneNumberError('Phone number must be between 11 and 13 digits.');
            return false;
        }
        setPhoneNumberError("");
        return true;
    };

    const validateSubject = (): boolean => {
        if (!subject.trim()) {
            setSubjectError("Subject is required.");
            return false;
        }
        setSubjectError("");
        return true;
    };

    const validateMessage = (): boolean => {
        if (!message.trim()) {
            setMessageError("Message is required.");
            return false;
        }
        setMessageError("");
        return true;
    };

    const handleValidateForm = (): boolean => {
        const isFirstNameValid = validateFirstName();
        const isLastNameValid = validateLastName();
        const isPhoneNumberValid = validatePhoneNumber();
        const isSubjectValid = validateSubject();
        const isMessageValid = validateMessage();

        return isFirstNameValid && isLastNameValid && isPhoneNumberValid && isSubjectValid && isMessageValid;
    };

    const handlePhoneNumberChange = (value: string) => {
        setPhoneNumber(value);

        if (value.length < 11 || value.length > 13) {
            setHasError(true);
            setPhoneNumberError('Phone number must be between 11 and 13 digits.');
        } else {
            setHasError(false);
            setPhoneNumberError('');
        }
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!handleValidateForm()) {
            return;
        }

        setLoading(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 1500));
            setLoading(false);
            setSuccessMessage("Message sent successfully. We will contact you soon.");
            setShowAlert(true);
            handleClearFormFields();

            setTimeout(() => {
                setSuccessMessage("");
                setShowAlert(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }, 2000);
        } catch (error) {
            setLoading(false);
            alert("There was an error sending the message.");
        }
    };

    const handleClearFormFields = () => {
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setSubject("");
        setMessage("");

        setFirstNameError("");
        setLastNameError("");
        setPhoneNumberError("");
        setSubjectError("");
        setMessageError("");
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />

            {/* Contact us initial section */}
            <section className="page_initial_section contact_us_init_section overflow-hidden">
                <div className="container-md">
                    <div className="page_heading_area">
                        <Slide direction="up" triggerOnce>
                            <h2 className="page_heading text-center">
                                Get in Touch With Us
                            </h2>
                        </Slide>
                        <Slide direction="up" delay={100} triggerOnce>
                            <p className="page_desc text-center">
                                Have a question but aren’t sure who to contact? Fill in the form to get in touch and a member of our team will reach out to you at the earliest.
                            </p>
                        </Slide>
                    </div>

                    <div className="contact_us_content">
                        <div className="row contact_content_row">
                            <div className="col-12 col-xl-6 col-lg-7">
                                <div className="contact_form_section">
                                    <div className="row contact_form_row">
                                        <div className="col-12 col-sm-6">
                                            <Slide direction="up" triggerOnce>
                                                <fieldset className="contact_input_container">
                                                    <label htmlFor="firstName" className="is_required">First Name</label>
                                                    <input
                                                        type="text"
                                                        id="firstName"
                                                        name="firstName"
                                                        value={firstName}
                                                        placeholder="Write"
                                                        autoComplete="off"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                                        required
                                                    />
                                                    {firstNameError && firstName === '' && (
                                                        <small className="contact_error_msg">{firstNameError}</small>
                                                    )}
                                                </fieldset>
                                            </Slide>
                                        </div>

                                        <div className="col-12 col-sm-6">
                                            <Slide direction="up" delay={50} triggerOnce>
                                                <fieldset className="contact_input_container">
                                                    <label htmlFor="lastName" className="is_required">Last Name</label>
                                                    <input
                                                        type="text"
                                                        id="lastName"
                                                        name="lastName"
                                                        value={lastName}
                                                        placeholder="Write"
                                                        autoComplete="off"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                                        required
                                                    />
                                                    {lastNameError && lastName === '' && (
                                                        <small className="contact_error_msg">{lastNameError}</small>
                                                    )}
                                                </fieldset>
                                            </Slide>
                                        </div>

                                        <div className="col-12 col-sm-6">
                                            <Slide direction="up" delay={100} triggerOnce>
                                                <fieldset className="contact_input_container">
                                                    <label htmlFor="phoneNumber" className="is_required">Phone Number</label>
                                                    <input
                                                        type="number"
                                                        id="phoneNumber"
                                                        name="phoneNumber"
                                                        value={phoneNumber}
                                                        placeholder="Write"
                                                        autoComplete="off"
                                                        onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                                        required
                                                    />
                                                    {phoneNumberError && (
                                                        <small className="contact_error_msg">{phoneNumberError}</small>
                                                    )}
                                                </fieldset>
                                            </Slide>
                                        </div>

                                        <div className="col-12 col-sm-6">
                                            <Slide direction="up" delay={150} triggerOnce>
                                                <fieldset className="contact_input_container">
                                                    <label htmlFor="subject" className="is_required">Subject</label>
                                                    <input
                                                        type="text"
                                                        id="subject"
                                                        name="subject"
                                                        value={subject}
                                                        placeholder="Write"
                                                        autoComplete="off"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
                                                        required
                                                    />
                                                    {subjectError && subject === '' && (
                                                        <small className="contact_error_msg">{subjectError}</small>
                                                    )}
                                                </fieldset>
                                            </Slide>
                                        </div>

                                        <div className="col-12">
                                            <Slide direction="up" delay={200} triggerOnce>
                                                <fieldset className="contact_input_container">
                                                    <label htmlFor="message" className="is_required">Message</label>
                                                    <textarea
                                                        name="message"
                                                        id="message"
                                                        value={message}
                                                        placeholder="Write"
                                                        autoComplete="off"
                                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                                                        required
                                                    ></textarea>
                                                    {messageError && message === '' && (
                                                        <small className="contact_error_msg">{messageError}</small>
                                                    )}
                                                </fieldset>
                                            </Slide>
                                        </div>
                                    </div>
                                    <Fade delay={500} triggerOnce>
                                        <button className="contact_btn btn_float mob_btn p-ripple"
                                            onClick={handleSendMessage}>
                                            {loading && (<ProgressSpinner strokeWidth="6" animationDuration="0.8s" />)}
                                            <span>Submit</span>
                                            <Ripple />
                                        </button>
                                    </Fade>
                                </div>
                            </div>

                            <div className="col-12 col-xl-6 col-lg-5">
                                <Slide direction="up" triggerOnce className="h-100">
                                    <div className="contact_us_img_area">
                                        <img src="/assets/images/website/contact-page/contact_us_img.webp" loading="lazy" alt="Traqdo" />
                                    </div>
                                </Slide>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact details section */}
            <section className="contact_details_section section__padding pt-0">
                <div className="container-md">
                    <div className="contact_details_content">
                        <div className="row contact_details_row">
                            {/* <div className="col-12 col-md-4 pe-md-2 pe-lg-3">
                                <Tilt tiltMaxAngleX={8} tiltAxis="x" tiltMaxAngleY={8} className="tile_effect h-100">
                                    <Slide direction="up" triggerOnce className="h-100">
                                        <article className="contact_detail_container first">
                                            <img src="/assets/images/website/contact-page/call_icon.svg" alt="Call Us" loading="lazy" />
                                            <h6>Call Us</h6>
                                            <Link to={'tel:+447462184536'}>UK +44 746 218 4536</Link>
                                            <Link to={'tel:+945678900'}>SL +945678900</Link>
                                        </article>
                                    </Slide>
                                </Tilt>
                            </div> */}

                            <div className="col-12 col-md-4 pe-md-2 pe-lg-3 ps-md-2 ps-lg-3">
                                <Tilt tiltMaxAngleX={8} tiltAxis="x" tiltMaxAngleY={8} className="tile_effect h-100">
                                    <Slide direction="up" delay={50} triggerOnce className="h-100">
                                        <article className="contact_detail_container second">
                                            <img src="/assets/images/website/contact-page/email_icon.svg" alt="Message Us" loading="lazy" />
                                            <h6>Email Us</h6>
                                            {/* <Link to={'mailto:traqdo@gmail.com'}>Traqdo@gmail.com</Link> */}
                                            <Link to={'mailto:info@nuvay.co.uk'}>info@nuvay.co.uk</Link>
                                        </article>
                                    </Slide>
                                </Tilt>
                            </div>

                            {/* <div className="col-12 col-md-4 ps-md-2 ps-lg-3">
                                <Tilt tiltMaxAngleX={8} tiltAxis="x" tiltMaxAngleY={8} className="tile_effect h-100">
                                    <Slide direction="up" delay={100} triggerOnce className="h-100">
                                        <article className="contact_detail_container third">
                                            <img src="/assets/images/website/contact-page/location_icon.svg" alt="Visit Us" loading="lazy" />
                                            <h6>Visit Us</h6>
                                            <Link to={'https://maps.app.goo.gl/6rxBgCqLWb9zgs6LA'} target="_blank">
                                                Unit 11, Rockware Business Centre <br /> Greenford, Middlesex, UB6 0AA.
                                            </Link>
                                        </article>
                                    </Slide>
                                </Tilt>
                            </div> */}
                        </div>
                    </div>
                </div>
            </section>

            {/* Map section */}
            {/* <section className="section__padding pt-0 map_section">
                <Fade triggerOnce>
                    <article className="map_container">
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.410189472779!2d-0.34891611241947085!3d51.542377035501644!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487612ee42e46b49%3A0xc343f2a317975810!2sRockware%20Business%20Centre%2C%205%20Rockware%20Ave%2C%20Greenford%20UB6%200AA%2C%20UK!5e0!3m2!1sen!2slk!4v1729595238134!5m2!1sen!2slk"
                            className="map_frame"
                            title="Nuvay Consultants(pvt).Ltd"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen={true}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </article>
                </Fade>
            </section> */}

            {/* Show success message */}
            <div className={`message_backdrop ${showAlert ? 'show' : ''}`} onClick={handleCloseAlert}>
                <div className={`message_box blurred ${showAlert ? 'show' : ''}`} onClick={e => e.stopPropagation()}>
                    <div className="message_box_content">
                        <i className="bi bi-check-circle-fill message_box_icon"></i>
                        <h6 className="message_box_message text-dark font_weight_normal">
                            {successMessage}
                        </h6>
                    </div>
                </div>
            </div>
            {/*  */}
        </React.Fragment>
    )
}

export default ContactUs;